import {
  ReactElement,
  useState,
  Ref,
  useRef,
  forwardRef,
  useImperativeHandle,
  InputHTMLAttributes,
} from "react";
import {
  Controller,
  Control,
  FieldValues,
  FieldPath,
  RegisterOptions,
} from "react-hook-form";
import { ZXCVBNResult } from "zxcvbn";
import { t } from "@lingui/core/macro";

import { FormField, FormFieldProps } from "_/components/form-field";
import { Overlay } from "_/components/overlay";

import { palette } from "_/style";

import * as S from "./styled";

// Constrain the available types for the input.
type InputType =
  | "text"
  | "password"
  | "number"
  | "tel"
  | "email"
  | "url"
  | "date";

type NumberOnChange = undefined | ((value: number | "") => void);
type StringOnChange = undefined | ((value: string) => void);

type InputProps = (FormFieldProps &
  Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "type"> &
  (
    | {
        /** Input type used for the underlying input element. */
        type?: "number";

        /**
         * Handlers for numeric changes. Note that "" is also passed for
         * blank inputs.
         * */
        onChange?: NumberOnChange;
      }
    | {
        /** Input type used for the underlying input element. */
        type?: Exclude<InputType, "number">;

        /**
         * Handlers for non-numeric text controls.
         */
        onChange?: StringOnChange;
      }
  )) & {
  /** True if border should be drawn around element. */
  border?: boolean;

  /** Icon to be displayed to the left of the input text.*/
  iconLeft?: ReactElement;

  /** Icon to be displayed to the right of the input text.*/
  iconRight?: ReactElement;

  /** For InputType 'password', when set to true, uses right icon as password visibility toggle. */
  togglePasswordVisibility?: boolean;

  /** Sets state as invalid when TextField is being used as a controlled component.*/
  invalid?: boolean;
};

export const TextField = forwardRef(function TextField(
  {
    type = "text",
    border = true,
    iconLeft,
    iconRight,
    invalid,
    style,
    className,
    togglePasswordVisibility = true,
    label,
    hint,
    help,
    placeholder = type === "password" ? t`common.password` : undefined,
    onChange,
    ...restProps
  }: InputProps,
  ref: Ref<HTMLInputElement | null>
): ReactElement {
  const [touched, setTouched] = useState(false);
  const [focused, setFocused] = useState(false);

  const [showPassword, setShowPassword] = useState(false);

  // get reference to input element to check invalid state
  // that can be passed up to the wrapper div
  const inputRef = useRef<HTMLInputElement>(null);

  // associate the forwarded ref with the internal reference
  // to the input element
  useImperativeHandle(ref, () => inputRef.current!, []);

  // When generating a random ID if none is provided, cache the value to
  // prevent unnecessary re-renders.
  const [id, _] = useState(restProps.id || crypto.randomUUID());

  const isInvalid = invalid || (!inputRef.current?.checkValidity() && touched);

  const passwordVisibilityToggle = (
    <S.ClickableIcon
      tabIndex={-1}
      variant={showPassword ? "VisibilityOff" : "Visibility"}
      onClick={() => setShowPassword(!showPassword)}
    />
  );

  // Type to use for the input element - use the provided type except in the case of a password,
  // in which case set the input type either to 'text' or 'password' based on the value of 'showPassword'.
  const computedType =
    type === "password" ? (showPassword ? "text" : "password") : type;

  // Compute how to set the right icon - if the provided input type is 'password' and the togglePasswordVisibility prop is set to true,
  // use passwordVisibilityToggle as right icon, even if another icon is provided for iconRight.
  let computedIconRight = iconRight;
  if (type === "password" && togglePasswordVisibility) {
    computedIconRight = passwordVisibilityToggle;
  }

  // The `className` prop must be provided to the wrapper to allow for
  // extending a component with styled-components.
  const textInput = (
    <S.Wrapper
      $invalid={isInvalid}
      $border={border}
      $disabled={restProps.disabled}
      $focused={focused}
      className={className}
      style={style}
    >
      {iconLeft}
      <S.Input
        {...restProps}
        ref={inputRef}
        id={id}
        disabled={restProps.disabled}
        placeholder={placeholder}
        type={computedType}
        onChange={(e) => {
          const value: string = e.target.value;

          // AFAIK, there's not really a good way to teach TS about the
          // correlation between the type of the input and the type of the
          // onChange handler. So casts are needed below.
          if (type === "number") {
            const numValue = value === "" ? ("" as const) : Number(value);
            // oxlint-disable-next-line typescript/no-unsafe-type-assertion
            (onChange as NumberOnChange)?.(numValue);
          } else {
            // oxlint-disable-next-line typescript/no-unsafe-type-assertion
            (onChange as StringOnChange)?.(value);
          }
        }}
        onFocus={(e) => {
          setFocused(true);
          restProps.onFocus?.(e);
        }}
        onBlur={(e) => {
          setFocused(false);
          setTouched(true);
          restProps.onBlur?.(e);
        }}
      />
      {computedIconRight}
    </S.Wrapper>
  );

  return (
    <FormField hint={hint} invalid={isInvalid} label={label} help={help}>
      {textInput}
    </FormField>
  );
});

interface FormTextFieldProps<
  T extends FieldValues,
  K extends FieldPath<T>,
> extends Omit<InputProps, "invalid" | "name" | "hint" | "type"> {
  rules?: Omit<
    RegisterOptions<T, K>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  name: K;
  control: Control<T, K>;
  type?: InputType | "new-password";
}

interface FormPlainTextFieldProps<
  T extends FieldValues,
  K extends FieldPath<T>,
> extends Omit<FormFieldProps, "invalid"> {
  name: K;
  control: Control<T, K>;
}

type PasswordStrengthIndicatorProps = {
  /** Password strength score number between 0 and 4. */
  score: number;
};

const GREY = palette.anchorGrey;

/**
 * Overlay content for password validation textfield.
 * Displays password strength score and suggestions to make the password stronger.
 */
const PasswordStrengthIndicator = ({
  score,
}: PasswordStrengthIndicatorProps): ReactElement => {
  // Options for label text and block / label color.
  const outcomes = [
    [t`components.password-strength-indicator.very-weak`, palette.tomatoRed],
    [t`components.password-strength-indicator.weak`, palette.pineappleYellow],
    [t`components.password-strength-indicator.strong`, palette.leafGreen],
    [t`components.password-strength-indicator.very-string`, palette.kellyGreen],
  ];

  // map scores to the block indices
  // treat scores of 0 or 1 the same (very weak)
  const adjustedScore = score <= 0 ? score : score - 1;

  const [label, color] =
    adjustedScore >= 0 ? outcomes[adjustedScore] : ["", GREY];

  function renderBlock(index: number) {
    const colorInner =
      adjustedScore >= index ? outcomes[adjustedScore][1] : GREY;

    return <S.Block $color={colorInner} />;
  }

  return (
    <S.ScoreWrapper>
      {renderBlock(0)}
      {renderBlock(1)}
      {renderBlock(2)}
      {renderBlock(3)}
      <S.ScoreLabel $color={color}>{label}</S.ScoreLabel>
    </S.ScoreWrapper>
  );
};

type PasswordFieldProps = Omit<InputProps, "type" | "onChange"> & {
  onValidate?: (validate: boolean) => void;
  onChange?: StringOnChange;
};

/**
 * Modified password TextField component with password validation overlay.
 */
export const PasswordField = forwardRef(function PasswordField(
  { invalid, onChange, onValidate, ...rest }: PasswordFieldProps,
  ref: Ref<HTMLInputElement | null>
) {
  const [validationResult, setValidationResult] = useState<ZXCVBNResult>();
  const [passwordValid, setPasswordValid] = useState(true);
  const [focused, setFocused] = useState(false);

  // Dynamically import "zxcvbn" to avoid loading the ~400kb module as part of the initial bundle.
  // See details here: https://github.com/dropbox/zxcvbn?tab=readme-ov-file#script-load-latency
  // Initiate the load when the component is first rendered in order to avoid latency when it is
  // called in the `validate()` function.
  const zxcvbnPromise = import("zxcvbn");

  function validate(value: string) {
    const password = value;

    void zxcvbnPromise.then((zxcvbn) => {
      const result = zxcvbn.default(password);
      const isValid = result.score >= 3;

      setValidationResult(result);
      setPasswordValid(isValid);

      onChange?.(password);
      onValidate?.(isValid);
    });
  }

  const textField = (
    <TextField
      {...rest}
      ref={ref}
      type="password"
      invalid={invalid || !passwordValid}
      onChange={validate}
      autoComplete="new-password"
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );

  const validationDisplay = validationResult ? (
    <S.ValidationDisplayWrapper>
      <PasswordStrengthIndicator
        score={
          validationResult.sequence.length > 0 ? validationResult.score : -1
        }
      />
      {validationResult.feedback.warning && (
        <S.WarningContainer>
          <S.WarningIcon variant={"Warning"} />
          {validationResult.feedback.warning}
        </S.WarningContainer>
      )}
      {!!validationResult.feedback.suggestions.length && (
        <S.SuggestionsContainer>
          <p>{t`components.password-strength-indicator.suggestions`}:</p>
          <S.SuggestionList>
            {validationResult.feedback.suggestions.map(
              (suggestion: string, i: number) => (
                <li key={i}>{suggestion}</li>
              )
            )}
          </S.SuggestionList>
        </S.SuggestionsContainer>
      )}
    </S.ValidationDisplayWrapper>
  ) : null;

  return (
    <Overlay
      target={textField}
      content={validationDisplay}
      placement={"leftStart"}
      delay={0}
      wait={0}
      keepOpen={focused}
      fullWidth
    />
  );
});

/**
 * DRY text field component for use with react-hook-form.
 */
export function FormTextField<T extends FieldValues, K extends FieldPath<T>>(
  props: FormTextFieldProps<T, K>
) {
  const [valid, setValid] = useState(false);
  const { name, control, rules, type, onChange: _unused, ...rest } = props;

  // Use password validation over provided validation callback if
  // the type of form field is specifically `"new-password"`
  const combinedRules = {
    ...rules,
    validate:
      type === "new-password"
        ? () => valid || t`components.password-form-field.validation-failed`
        : rules?.validate,
  };

  return (
    <Controller
      control={control}
      rules={combinedRules}
      name={name}
      render={({ field, fieldState }) =>
        type === "new-password" ? (
          <PasswordField
            {...rest}
            {...field}
            value={field.value ?? ""}
            onChange={field.onChange}
            onValidate={setValid}
            invalid={fieldState.invalid}
            hint={fieldState.error?.message}
          />
        ) : (
          <TextField
            {...rest}
            {...field}
            value={field.value ?? ""}
            type={type}
            invalid={fieldState.invalid}
            hint={fieldState.error?.message}
          />
        )
      }
    />
  );
}

/**
 * Read-only plain text that is formatted the same as FormTextField, to make it
 * easier to display read-only text in a form using similar code as writable
 * fields.
 */
export function FormPlainTextField<
  T extends FieldValues,
  K extends FieldPath<T>,
>(props: FormPlainTextFieldProps<T, K>) {
  const { control, hint, label, help } = props;

  // react-hook-form's `FieldValues` type is not generic, so cast needed.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion
  const value = (control._formValues as Record<K, string>)[props.name];

  return (
    <FormField hint={hint} label={label} help={help}>
      {value}
    </FormField>
  );
}
